'use client';

import { useState, useEffect } from 'react';
import Link from 'next/link';
import { motion } from 'framer-motion';
import { Send, ArrowUp } from 'lucide-react';
import { FaGithub, FaLinkedinIn, FaTwitter, FaInstagram } from 'react-icons/fa';
import toast from 'react-hot-toast';

const SOCIALS = [
  { icon: FaGithub, href: 'https://github.com/nasifniloy', label: 'GitHub', color: '#333' },
  { icon: FaLinkedinIn, href: 'https://linkedin.com/in/nasifniloy', label: 'LinkedIn', color: '#0A66C2' },
  { icon: FaTwitter, href: 'https://twitter.com/nasifniloy', label: 'Twitter', color: '#1DA1F2' },
  { icon: FaInstagram, href: 'https://instagram.com/nasifniloy', label: 'Instagram', color: '#E4405F' },
];

const QUICK_LINKS = [
  { href: '/', label: 'Home' },
  { href: '/about', label: 'About' },
  { href: '/projects', label: 'Projects' },
  { href: '/gallery', label: 'Gallery' },
  { href: '/blog', label: 'Blog' },
  { href: '/contact', label: 'Contact' },
];

const SERVICES = [
  { href: '/services#web', label: 'Web Development' },
  { href: '/services#api', label: 'API Development' },
  { href: '/services#uiux', label: 'UI/UX Design' },
  { href: '/services#consulting', label: 'Tech Consulting' },
];

export default function Footer() {
  const [email, setEmail] = useState('');
  const [subscribing, setSubscribing] = useState(false);
  const [showTopBtn, setShowTopBtn] = useState(false);

  useEffect(() => {
    const handleScroll = () => setShowTopBtn(window.scrollY > 400);
    window.addEventListener('scroll', handleScroll);
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleSubscribe = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      toast.error('Please enter a valid email address');
      return;
    }
    setSubscribing(true);
    try {
      await new Promise((r) => setTimeout(r, 700));
      toast.success('Thanks for subscribing!');
      setEmail('');
    } catch {
      toast.error('Subscription failed. Try again later.');
    } finally {
      setSubscribing(false);
    }
  };

  const scrollTop = () => {
    if (typeof window !== 'undefined') {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  return (
    <footer className="footer">
      <div className="footer-container">
        <div className="footer-grid">
          {/* Brand */}
          <motion.div
            className="footer-col footer-brand"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <Link href="/" className="footer-logo">
              <span className="footer-logo-text">NAN</span>
            </Link>
            <p className="footer-tagline">
              Full-Stack Developer crafting modern, performant web experiences with clean code and
              pixel-perfect design.
            </p>

            <div className="footer-contact-list">
              <a href="mailto:hello@nasifahammedniloy.com" className="footer-contact-item">
                hello@nasifahammedniloy.com
              </a>
              <a href="tel:+8801947527775" className="footer-contact-item">
                +880 1947-527775
              </a>
            </div>

            <div className="footer-socials">
              {SOCIALS.map((s) => (
                <a
                  key={s.label}
                  href={s.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={s.label}
                  className="footer-social-btn"
                  style={{ '--hover-color': s.color } as React.CSSProperties}
                >
                  <s.icon size={16} />
                </a>
              ))}
            </div>
          </motion.div>

          {/* Quick Links */}
          <motion.div
            className="footer-col"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.1 }}
          >
            <h3 className="footer-heading">Quick Links</h3>
            <ul className="footer-list">
              {QUICK_LINKS.map((link) => (
                <li key={link.href}>
                  <Link href={link.href} className="footer-link">
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </motion.div>

          {/* Services */}
          <motion.div
            className="footer-col"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            <h3 className="footer-heading">Services</h3>
            <ul className="footer-list">
              {SERVICES.map((s) => (
                <li key={s.href}>
                  <Link href={s.href} className="footer-link">
                    {s.label}
                  </Link>
                </li>
              ))}
            </ul>
          </motion.div>

          {/* Newsletter */}
          <motion.div
            className="footer-col footer-newsletter-col"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.3 }}
          >
            <h3 className="footer-heading">Newsletter</h3>
            <p className="footer-newsletter-text">
              Get the latest articles, tips and tutorials delivered straight to your inbox.
            </p>
            <form className="footer-newsletter-form" onSubmit={handleSubscribe}>
              <input
                type="email"
                className="footer-newsletter-input"
                placeholder="your@email.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                aria-label="Email address"
              />
              <button
                type="submit"
                className="footer-newsletter-btn"
                disabled={subscribing}
                aria-label="Subscribe"
              >
                <Send size={16} />
                <span>{subscribing ? 'Sending...' : 'Subscribe'}</span>
              </button>
            </form>
          </motion.div>
        </div>

        <div className="footer-divider" />

        <div className="footer-bottom">
          <div className="footer-copy">
            &copy; 2026 Nasif Ahammed Niloy. All rights reserved.
          </div>

          <div className="footer-bottom-links">
            <Link href="/privacy" className="footer-bottom-link">Privacy</Link>
            <span className="footer-bottom-sep" />
            <Link href="/terms" className="footer-bottom-link">Terms</Link>
          </div>

          <div className="footer-made">
            <span className="footer-made-dot" />
            <span>Crafted in</span>
            <span className="footer-made-place">Dhaka</span>
            <span className="footer-made-sep">·</span>
            <span>Powered by</span>
            <span className="footer-made-tech">caffeine</span>
            <span className="footer-made-emoji">☕</span>
            <span>&amp; curiosity</span>
          </div>
        </div>
      </div>

      <button
        className={`footer-top-btn ${showTopBtn ? 'footer-top-btn-visible' : ''}`}
        onClick={scrollTop}
        aria-label="Back to top"
        type="button"
      >
        <ArrowUp size={18} />
      </button>
    </footer>
  );
}
